// Exact rational arithmetic over bigint (D-08: no JS floats on any money path).
//
// Money itself is integer minor units or micros (`bigint`, spec 11 §Money representation).
// Rationals carry the quantities that are not integers: FX rates, elapsed fractions, threshold
// fractions, run-rate projections. A rational is rounded to an integer exactly once, at the
// point where it becomes money again, half to even (the posting rule of review fix #18).

/** `n / d` in lowest terms with `d > 0`. Build with `rat`; never construct by hand. */
export interface Rational {
  readonly n: bigint;
  readonly d: bigint;
}

function gcd(a: bigint, b: bigint): bigint {
  let x = a < 0n ? -a : a;
  let y = b < 0n ? -b : b;
  while (y !== 0n) [x, y] = [y, x % y];
  return x;
}

export function rat(n: bigint, d = 1n): Rational {
  if (d === 0n) throw new RangeError("rational denominator is zero");
  const sign = d < 0n ? -1n : 1n;
  const g = gcd(n, d) || 1n;
  return { n: (sign * n) / g, d: (sign * d) / g };
}

export const ZERO: Rational = rat(0n);
export const ONE: Rational = rat(1n);

const DECIMAL = /^([+-]?)(\d+)(?:\.(\d+))?$/;

/** Exact decimal text (`"1.0825"`, `"-3"`) → rational. No exponent, no rounding, ≤ 64 chars. */
export function parseDecimal(text: string): Rational {
  if (text.length > 64) throw new RangeError("decimal is too long");
  const m = DECIMAL.exec(text);
  if (!m) throw new RangeError(`not a decimal: ${JSON.stringify(text)}`);
  const frac = m[3] ?? "";
  const digits = BigInt((m[2] ?? "") + frac);
  return rat(m[1] === "-" ? -digits : digits, 10n ** BigInt(frac.length));
}

export function add(a: Rational, b: Rational): Rational {
  return rat(a.n * b.d + b.n * a.d, a.d * b.d);
}
export function sub(a: Rational, b: Rational): Rational {
  return rat(a.n * b.d - b.n * a.d, a.d * b.d);
}
export function mul(a: Rational, b: Rational): Rational {
  return rat(a.n * b.n, a.d * b.d);
}
export function div(a: Rational, b: Rational): Rational {
  if (b.n === 0n) throw new RangeError("division by zero");
  return rat(a.n * b.d, a.d * b.n);
}
export function inverse(a: Rational): Rational {
  return div(ONE, a);
}
export function cmp(a: Rational, b: Rational): -1 | 0 | 1 {
  const l = a.n * b.d;
  const r = b.n * a.d;
  return l < r ? -1 : l > r ? 1 : 0;
}
export function isPositive(a: Rational): boolean {
  return a.n > 0n;
}

/** Nearest integer, ties to even. */
export function roundHalfEven(a: Rational): bigint {
  const negative = a.n < 0n;
  const mag = negative ? -a.n : a.n;
  let q = mag / a.d;
  const twice = (mag % a.d) * 2n;
  if (twice > a.d || (twice === a.d && q % 2n === 1n)) q += 1n;
  return negative ? -q : q;
}

/** Smallest integer ≥ a. */
export function ceil(a: Rational): bigint {
  const q = a.n / a.d;
  return a.n > 0n && a.n % a.d !== 0n ? q + 1n : q;
}

/** Exact decimal text when the denominator divides a power of ten, else rounded half-even. */
export function toDecimal(a: Rational, places: number): string {
  const scaled = roundHalfEven(mul(a, rat(10n ** BigInt(places))));
  const negative = scaled < 0n;
  const mag = (negative ? -scaled : scaled).toString().padStart(places + 1, "0");
  const body = places === 0 ? mag : `${mag.slice(0, -places)}.${mag.slice(-places)}`;
  return negative ? `-${body}` : body;
}
